import { useState, type ReactNode } from "react";
import type { DirectionGroup } from "@/lib/types/recipe";

// Re-export so existing `from "@/features/DirectionsList/DirectionsList"` imports
// keep resolving after the type moved to lib/types/recipe.
export type { Direction } from "@/lib/types/recipe";

interface DirectionsListProps {
    directions: DirectionGroup[];
    notes?: string; // Each note is a string, rendered as an ordered list if present
    storage?: string; // Optional storage instructions
    afterDirections?: ReactNode; // Slot rendered between the steps and the notes/storage divider
}

export const DirectionsList = (props: DirectionsListProps) => {
    const { directions, notes, storage, afterDirections } = props;
    const hasNotesOrStorage = (notes && notes.length > 0) || storage;
    // Completion is tracked by a stable "groupIndex-stepIndex" key so steps
    // stay distinct across groups (numbering restarts per group).
    const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

    const toggleStep = (id: string) => {
        setCompletedSteps((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <div>
            <h2 className="text-3xl mb-4">Directions</h2>
            {directions.map((group, groupIndex) => (
                <div key={group.id ?? groupIndex} className="mb-6 last:mb-0">
                    {group.title && (
                        <h3 className="text-xl tracking-wide mb-3 pb-1 border-b border-accent-200 inline-block pr-6">
                            {group.title}
                        </h3>
                    )}
                    {/* counter-reset per group restarts numbering at 1 */}
                    <ol className="text-lg list-none ml-8 [counter-reset:li]">
                        {group.steps.map((dir, stepIndex) => {
                            const id = `${groupIndex}-${stepIndex}`;
                            const isCompleted = completedSteps.has(id);
                            return (
                                <li
                                    key={id}
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={isCompleted}
                                    onClick={() => toggleStep(id)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            toggleStep(id);
                                        }
                                    }}
                                    className="list-style-none border-l border-default-300 last:border-l-transparent relative pb-8 last:pb-0 leading-relaxed cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/40 before:absolute before:top-[-2px] before:left-[-18px] before:flex before:items-center before:justify-center before:w-9 before:h-9 before:rounded-full before:bg-primary before:text-primary-foreground before:text-base before:font-semibold before:content-[counter(li)] before:[counter-increment:li] pl-7"
                                >
                                    <span
                                        className={`transition-colors duration-200 ${
                                            isCompleted ? "line-through italic text-default-400" : ""
                                        }`}
                                    >
                                        {dir.step}
                                    </span>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            ))}
            {afterDirections && <div className="my-6">{afterDirections}</div>}
            {hasNotesOrStorage && (
                <div className="flex items-center gap-3 my-6 mx-auto w-9/10 text-accent-600" aria-hidden="true">
                    <span className="h-px flex-1 bg-accent-200" />
                    <span className="text-base leading-none select-none">❦</span>
                    <span className="h-px flex-1 bg-accent-200" />
                </div>
            )}
            {notes && notes.length > 0 && (
                <div className="mb-4">
                    <h3 className="text-xl mb-2">Notes</h3>
                    <p className="ml-12 leading-relaxed">{notes}</p>
                </div>
            )}
            {storage && (
                <div>
                    <h3 className="text-xl mb-1">Storage</h3>
                    <p className="ml-12 leading-relaxed">{storage}</p>
                </div>
            )}
        </div>
    );
};
